/**
 * @file src/client/index.ts
 * @description نقطة الدخول الرئيسية لعميل الواجهة
 * @module client
 * @version 1.0.0
 * @author Dueli Team
 */

// CSP delegation (C5: data-csp-* dispatch for all former inline handlers)
import './csp-delegate';

// Core
import { State } from './core/State';
import { ApiClient } from './core/ApiClient';
import { CookieUtils } from './core/CookieUtils';

// Services
import { AuthService } from './services/AuthService';
import { ThemeService } from './services/ThemeService';

// Streaming Services
import { P2PConnection } from './services/P2PConnection';
import { SignalingManager } from './services/SignalingManager';
import { VideoCompositor } from './services/VideoCompositor';
import { ChunkUploader, PlatformRecordingLease } from './services/ChunkUploader';

// Note: Test Streaming functions are now injected as inline scripts in test pages
// See: modules/pages/test/scripts/client/shared.ts

// Countries (shared data)
import { getCountriesList, getCountry } from '../countries';
import { t, isRTL } from '../i18n';

// UI
import { Toast } from './ui/Toast';
import { Modal } from './ui/Modal';
import { Menu } from './ui/Menu';
import { NotificationsUI } from './ui/NotificationsUI';
import { MessagesUI } from './ui/MessagesUI';
import { InteractionsUI } from './ui/InteractionsUI';
import { ScheduleUI } from './ui/ScheduleUI';
import { SettingsUI } from './ui/SettingsUI';
import { InvitePanel } from './ui/InvitePanel';
import { RecommendationCarousel, CompetitorsMiniStatsCard } from './ui/RecommendationCarousel';
import { CountdownTimer } from './ui/CountdownTimer';
import { WatchLaterUI } from './ui/WatchLaterUI';
import { RecordingDownloadPanel } from './ui/RecordingDownloadPanel';
import { openCompetitionShare, syncCompetitionShareMeta } from './ui/CompetitionSharePanel';
import { refreshI18nLayout } from './ui/I18nLayoutGuard';
import type { CompetitionShareInput } from '../lib/competitionShare';

// Shared Components (View layer - single source of truth)
import { getCompetitionCard, type CompetitionCardProps } from '../shared/components/competition-card';
import { getUserCard, getUserCards, type UserCardProps } from '../shared/components/user-card';
import { getUserAvatarLink, getProfilePath, type UserAvatarOptions } from '../shared/components/user-avatar';

// Pages
import { HomePage } from './pages/HomePage';

// Helpers
import { DateFormatter } from './helpers/DateFormatter';
import { NumberFormatter } from './helpers/NumberFormatter';
import { YouTubeHelpers } from './helpers/YouTubeHelpers';
import { Utils } from './helpers/Utils';
import {
    resolveCompetitorButtons,
    resolveCompetitorVideoLayout,
    resolveLiveAction,
    resolveVideoSurface,
} from './helpers/CompetitionSurface';

/**
 * Country selection functions (moved from CountryService)
 */
const CountryFunctions = {
    getCurrentCode(): string {
        return State.getCountry();
    },

    select(countryCode: string): void {
        const country = getCountry(countryCode);
        if (!country) return;

        CookieUtils.set('country', countryCode, 365);
        CookieUtils.set('lang', country.primaryLang, 365);

        if (State.currentUser) {
            ApiClient.put('/api/users/preferences', {
                country: countryCode,
                language: country.primaryLang
            }).catch(err => console.error('Failed to update preferences:', err));
        }

        window.location.href = `?lang=${country.primaryLang}`;
    },

    renderList(filter: string = ''): void {
        const container = document.getElementById('countriesList');
        if (!container) return;

        const currentCountry = this.getCurrentCode();
        const countries = getCountriesList(State.lang);
        const filtered = filter
            ? countries.filter(c =>
                c.nativeName.toLowerCase().includes(filter.toLowerCase()) ||
                c.code.toLowerCase().includes(filter.toLowerCase())
            )
            : countries;

        if (filtered.length === 0) {
            container.innerHTML = `
                <div class="p-4 text-center text-gray-400 text-sm">
                    ${t('search.no_results', State.lang)}
                </div>
            `;
            return;
        }

        container.innerHTML = filtered.map(country => `
            <button 
                data-csp-on="click" data-csp-fn="selectCountry" data-csp-args='[${JSON.stringify((country.code))}]' 
                class="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${country.code === currentCountry ? 'bg-purple-50 dark:bg-purple-900/30' : ''}"
            >
                <img src="https://flagcdn.com/w40/${country.code.toLowerCase()}.png" class="w-6 h-4 object-cover rounded-sm shadow-sm" alt="${country.code}">
                <span class="flex-1 ${country.rtl ? 'text-right' : 'text-left'} text-sm font-medium text-gray-900 dark:text-white">${country.nativeName}</span>
                ${country.code === currentCountry ? '<i class="fas fa-check text-purple-600 text-sm"></i>' : ''}
            </button>
        `).join('');
    }
};

/**
 * Main Application Class
 * التطبيق الرئيسي
 */
export class App {
    /**
     * Initialize the application
     */
    static async init(): Promise<void> {
        // Initialize state from URL params
        State.init();

        // Initialize theme
        ThemeService.init();

        // Check authentication (await to ensure State.currentUser is set before HomePage.init)
        await AuthService.checkAuth();

        // Expose the authenticated user (never a session secret) for SSR
        // inline scripts.
        window.currentUser = State.currentUser;
        window.lang = State.lang;
        window.isDarkMode = State.isDarkMode;

        // Setup menu click outside handlers
        Menu.setupClickOutside();

        // Handle OAuth callback from URL
        this.handleOAuthCallback();

        // Initialize Pages (safe to call as they check for DOM presence)
        HomePage.init();

        // UNIT32.1: hydrate explicit Watch Later toggles (cards + competition
        // page) from the authenticated server status endpoint.
        WatchLaterUI.init();

        refreshI18nLayout(document, State.lang);
        window.refreshI18nLayout = (root?: ParentNode) => refreshI18nLayout(root ?? document, State.lang);

        console.log('🔥 Dueli Client loaded successfully!');
    }

    /**
     * Handle OAuth error returned in the URL. Successful OAuth callbacks use
     * the HttpOnly cookie + `postMessage` path; no session is ever in a URL.
     */
    private static handleOAuthCallback(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');

        if (error) {
            Modal.showOAuthError(error);
            window.history.replaceState({}, document.title,
                window.location.pathname + window.location.search.replace(/[\?\&]error=[^\&]+/, '').replace(/^\&/, '?')
            );
        }
    }
}

// ============================================
// Global Exports for backward compatibility
// ============================================

declare global {
    interface Window {
        dueli: typeof dueliAPI;
        currentUser: any;
        lang: string;
        isDarkMode: boolean;
        // Global functions for onclick handlers
        showLoginModal: typeof Modal.showLogin;
        hideLoginModal: typeof Modal.hideLogin;
        switchAuthTab: typeof Modal.switchAuthTab;
        showForgotPassword: typeof Modal.showForgotPassword;
        showLogin: typeof Modal.showLoginForm;
        handleLogin: typeof AuthService.handleLogin;
        handleRegister: typeof AuthService.handleRegister;
        handleForgotPassword: typeof AuthService.handleForgotPassword;
        handleVerifyResetCode: typeof AuthService.handleVerifyResetCode;
        handleResetPassword: typeof AuthService.handleResetPassword;
        loginWith: typeof AuthService.loginWith;
        logout: typeof AuthService.logout;
        toggleDarkMode: typeof ThemeService.toggle;
        toggleUserMenu: typeof Menu.toggleUser;
        toggleCountryMenu: typeof Menu.toggleCountry;
        filterCountries: (query: string) => void;
        selectCountry: (code: string) => void;
        showHelp: typeof Modal.showHelp;
        checkAuth: typeof AuthService.checkAuth;
        authStatus: typeof AuthService.getAuthStatus;
        toggleNotifications: typeof NotificationsUI.toggle;
        markAllNotificationsRead: typeof NotificationsUI.markAllAsRead;
        toggleMessages: typeof MessagesUI.toggle;
        markAllMessagesRead: typeof MessagesUI.markAllRead;

        // Home Page Methods
        setMainTab: typeof HomePage.setMainTab;
        setSubTab: typeof HomePage.setSubTab;

        // Competition Card Renderer (View from shared components)
        renderCompetitionCard: (item: CompetitionCardProps, lang?: string) => string;
        renderCompetitionCards: (items: CompetitionCardProps[], lang?: string) => string;

        // User Card Renderer (View from shared components)
        renderUserCard: (user: UserCardProps, lang?: string) => string;
        renderUserCards: (users: UserCardProps[], lang?: string) => string;

        // Shared user avatar -> profile link (B6)
        renderUserAvatar: (opts: UserAvatarOptions) => string;
        profilePathFor: (username: unknown) => string | null;

        // Streaming Services (for live room page)
        P2PConnection: typeof P2PConnection;
        // Single shared signaling transport (test pages + production adapter)
        SignalingManager: typeof SignalingManager;
        VideoCompositor: typeof VideoCompositor;
        ChunkUploader: typeof ChunkUploader;

        // R4-COMP-UNIFIED-1: pure role×state surface decision (unified page)
        resolveVideoSurface: typeof resolveVideoSurface;
        // UNIT30.4: one tower entry, state/role-resolved action.
        resolveLiveAction: typeof resolveLiveAction;
        // UNIT30.4 §15.7–15.8: mutually-exclusive start/end + video layout.
        resolveCompetitorButtons: typeof resolveCompetitorButtons;
        resolveCompetitorVideoLayout: typeof resolveCompetitorVideoLayout;

        // R4-CHUNK-DOWNLOAD-1: independent recording download dialog
        RecordingDownloadPanel: typeof RecordingDownloadPanel;
        openRecordingDownload: (competitionId: number) => RecordingDownloadPanel | null;
        openCompetitionShare: () => void;
        syncCompetitionShareMeta: (comp: CompetitionShareInput) => void;
        refreshI18nLayout: (root?: ParentNode) => void;

        // UNIT32.1: CSP-delegated `WatchLaterUI.toggle` from shared cards.
        WatchLaterUI: typeof WatchLaterUI;

        // Invite Panel (Task 10)
        openInvitePanel: (competitionId: number) => void;
        closeInvitePanel: () => void;
        toggleInvitePanel: (competitionId: number) => void;

        // Note: Test Streaming functions are injected as inline scripts in test pages
    }
}

/**
 * Dueli API
 * واجهة برمجة التطبيق العامة
 */
const dueliAPI = {
    // Auth
    checkAuth: () => AuthService.checkAuth(),
    authStatus: () => AuthService.getAuthStatus(),
    updateAuthUI: () => AuthService.updateAuthUI(),
    loginWith: (provider: string) => AuthService.loginWith(provider),
    logout: () => AuthService.logout(),

    // Modal
    showLoginModal: () => Modal.showLogin(),
    hideLoginModal: () => Modal.hideLogin(),
    showHelp: () => Modal.showHelp(),

    // Theme
    toggleDarkMode: () => ThemeService.toggle(),

    // Menu
    toggleUserMenu: () => Menu.toggleUser(),
    toggleCountryMenu: () => Menu.toggleCountry(),

    // Toast
    showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => Toast.show(message, type),

    // Formatters
    formatDate: (date: string, lang?: string) => DateFormatter.format(date, lang),
    formatNumber: (num: number, lang?: string) => NumberFormatter.format(num, lang),
    formatTimeAgo: (date: string, lang?: string) => DateFormatter.timeAgo(date, lang),

    // Utils
    debounce: Utils.debounce,

    // API
    api: ApiClient.request,

    // YouTube
    youtubeHelpers: YouTubeHelpers,
};

// Set global window.dueli
if (typeof window !== 'undefined') {
    window.dueli = dueliAPI;

    // Global function bindings for onclick handlers in HTML
    window.showLoginModal = () => Modal.showLogin();
    window.hideLoginModal = () => Modal.hideLogin();
    window.switchAuthTab = (tab: 'login' | 'register') => Modal.switchAuthTab(tab);
    window.showForgotPassword = () => Modal.showForgotPassword();
    window.showLogin = () => Modal.showLoginForm();
    window.handleLogin = (e: Event) => AuthService.handleLogin(e);
    window.handleRegister = (e: Event) => AuthService.handleRegister(e);
    window.handleForgotPassword = (e: Event) => AuthService.handleForgotPassword(e);
    window.handleVerifyResetCode = (e: Event) => AuthService.handleVerifyResetCode(e);
    window.handleResetPassword = (e: Event) => AuthService.handleResetPassword(e);
    window.loginWith = (provider: string) => AuthService.loginWith(provider);
    window.logout = () => AuthService.logout();
    window.toggleDarkMode = () => ThemeService.toggle();
    window.toggleUserMenu = () => Menu.toggleUser();
    window.toggleCountryMenu = () => Menu.toggleCountry();
    window.filterCountries = (query: string) => CountryFunctions.renderList(query);
    window.selectCountry = (code: string) => CountryFunctions.select(code);
    window.showHelp = () => Modal.showHelp();
    window.checkAuth = () => AuthService.checkAuth();
    // Post-R1 acceptance: canonical auth resolution state for page guards
    // ('authenticated' | 'guest' | 'unknown'). Guards must treat 'unknown'
    // (fresh page + transient 429/5xx/network) as "retrying", never as guest.
    window.authStatus = () => AuthService.getAuthStatus();
    window.toggleNotifications = () => NotificationsUI.toggle();
    window.markAllNotificationsRead = () => NotificationsUI.markAllAsRead();
    window.toggleMessages = () => MessagesUI.toggle();
    window.markAllMessagesRead = () => MessagesUI.markAllRead();

    // C5: UI classes referenced by data-csp-fn delegation (CSP-safe, no inline handlers)
    (window as any).InteractionsUI = InteractionsUI;
    (window as any).MessagingUI = MessagesUI;
    (window as any).ScheduleUI = ScheduleUI;
    (window as any).SettingsUI = SettingsUI;
    (window as any).NotificationsUI = NotificationsUI;
    // UNIT32.1: CSP-delegated `WatchLaterUI.toggle` from shared cards.
    window.WatchLaterUI = WatchLaterUI;

    // Bind Home Page Methods
    window.setMainTab = (tab: any) => HomePage.setMainTab(tab);
    window.setSubTab = (tab: string) => HomePage.setSubTab(tab);

    // Bind Competition Card Renderer (uses shared View component)
    window.renderCompetitionCard = (item: CompetitionCardProps, lang: string = State.lang) => getCompetitionCard(item, lang);
    window.renderCompetitionCards = (items: CompetitionCardProps[], lang: string = State.lang) => items.map(item => getCompetitionCard(item, lang)).join('');

    // Bind User Card Renderer (uses shared View component)
    window.renderUserCard = (user: UserCardProps, lang: string = State.lang) => getUserCard(user, lang);
    window.renderUserCards = (users: UserCardProps[], lang: string = State.lang) => getUserCards(users, lang);

    // Bind the shared avatar -> profile link helper (B6)
    window.renderUserAvatar = (opts: UserAvatarOptions) =>
        getUserAvatarLink({ ...opts, lang: opts.lang || State.lang });

    // Canonical profile path builder, so page scripts never hand-roll the URL.
    window.profilePathFor = (username: unknown) => getProfilePath(username, State.lang);

    // Bind Streaming Services (for live room page)
    window.P2PConnection = P2PConnection;
    // R4-LIVE-INT-1: the single shared signaling transport — consumed by the
    // live test pages (host/guest) and by the production P2PConnection adapter.
    window.SignalingManager = SignalingManager;
    window.VideoCompositor = VideoCompositor;
    window.ChunkUploader = ChunkUploader;
    // R4-RELEASE-RECOVERY-C: platform lease client for the host upload path.
    (window as any).PlatformRecordingLease = PlatformRecordingLease;
    // R4-COMP-UNIFIED-1: pure role×state decision for the unified page.
    window.resolveVideoSurface = resolveVideoSurface;
    // UNIT30.4: one tower entry, state/role-resolved action.
    window.resolveLiveAction = resolveLiveAction;
    // UNIT30.4 §15.7–15.8: one visible tower/End action and the four
    // competitor video layouts (actual remote-frame evidence only).
    window.resolveCompetitorButtons = resolveCompetitorButtons;
    window.resolveCompetitorVideoLayout = resolveCompetitorVideoLayout;
    // R4-CHUNK-DOWNLOAD-1: independent download dialog (auth-gated by
    // isDownloadUserAuthenticated + the server manifest route).
    window.RecordingDownloadPanel = RecordingDownloadPanel;
    window.openRecordingDownload = (competitionId: number) => RecordingDownloadPanel.open(Number(competitionId));
    window.openCompetitionShare = () => { void openCompetitionShare(); };
    window.syncCompetitionShareMeta = syncCompetitionShareMeta;
    window.refreshI18nLayout = (root?: ParentNode) => refreshI18nLayout(root ?? document, State.lang);

    // Note: Test Streaming functions are injected as inline scripts in test pages
}

// Auto-initialize when DOM is ready
if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        App.init();
    });
}

// Export all modules
export { State, ApiClient, CookieUtils } from './core';
export { AuthService, ThemeService } from './services';
export { Toast, Modal, Menu } from './ui';
export { DateFormatter, NumberFormatter, YouTubeHelpers, Utils } from './helpers';
export { HomePage } from './pages/HomePage';

